import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { MentionsInput, Mention } from 'react-mentions';
import axios from 'axios';
import './NotesSection.css';

const NotesSection = ({
    notesList,
    currentNote,
    showAllNotes,
    onCurrentNoteChange,
    onAddNote,
    onToggleShowAll,
    onAddReply
}) => {
    const [users, setUsers] = useState([]);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [hoveredNoteIndex, setHoveredNoteIndex] = useState(null);
    const [expandedReplies, setExpandedReplies] = useState({});

    // Auto-expand replies when notes change
    useEffect(() => {
        const newExpanded = {};
        notesList.forEach(note => {
            if (note.Replies && note.Replies.length > 0) {
                // Keep existing expanded state, or default to collapsed
                newExpanded[note.RfqNoteId] = expandedReplies[note.RfqNoteId] || false;
            }
        });
        setExpandedReplies(newExpanded);
    }, [notesList]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get('/manage/users');
                const userSuggestions = response.data.map(user => ({
                    id: user.id,
                    display: `${user.firstname} ${user.lastname}`
                }));
                setUsers(userSuggestions);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, []);

    const renderMentionedText = (text) => {
        const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            parts.push(
                <span
                    key={match.index}
                    style={{
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontWeight: '500'
                    }}
                >
                    @{match[1]}
                </span>
            );
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    const mentionStyle = {
        control: {
            fontSize: '14px',
        },
        '&multiLine': {
            control: {
                minHeight: 75,
            },
            highlighter: {
                padding: '9px',
                border: '1px solid transparent',
            },
            input: {
                padding: '9px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                outline: 'none',
                fontSize: '14px',
            },
        },
        suggestions: {
            list: {
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                maxHeight: '200px',
                overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
            },
            item: {
                padding: '8px 12px',
                borderBottom: '1px solid #f3f4f6',
                '&focused': {
                    backgroundColor: '#f3f4f6',
                },
            },
        },
    };

    return (
        <div style={{ padding: '0 20px' }}>
            {notesList.length > 0 && (
                <div style={{ borderBottom: '1px solid #e5e7eb', paddingTop: '20px' }}>
                    {[...notesList]
                        .sort((a, b) => new Date(b.CreatedDateTime || 0) - new Date(a.CreatedDateTime || 0))
                        .slice(0, showAllNotes ? notesList.length : 3)
                        .map((note, index) => (
                            <div key={index}>
                                <div
                                    style={{
                                        backgroundColor: '#f9fafb',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        marginBottom: '10px',
                                        border: '1px solid #e5e7eb',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={() => setHoveredNoteIndex(index)}
                                    onMouseLeave={() => setHoveredNoteIndex(null)}
                                >
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#6b7280',
                                        marginBottom: '6px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <span>
                                            <strong style={{ color: '#374151', textTransform: 'capitalize' }}>
                                                {note.Username || 'Unknown'}
                                            </strong>
                                            {' • '}
                                            {note.CreatedDateTime ? new Date(note.CreatedDateTime).toLocaleString('en-US') : 'Just now'}
                                        </span>
                                        <i
                                            className="ti-comment-alt"
                                            onClick={() => {
                                                setReplyingTo(note);
                                                setReplyText('');
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#3b82f6',
                                                fontSize: '16px',
                                                padding: '4px',
                                                opacity: hoveredNoteIndex === index ? 1 : 0,
                                                transition: 'opacity 0.2s ease',
                                                pointerEvents: hoveredNoteIndex === index ? 'auto' : 'none'
                                            }}
                                            title="Reply to this note"
                                        />
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#1f2937',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {renderMentionedText(note.NoteText)}
                                    </div>

                                    {/* Reply count and toggle */}
                                    {note.Replies && note.Replies.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setExpandedReplies(prev => ({
                                                    ...prev,
                                                    [note.RfqNoteId]: !prev[note.RfqNoteId]
                                                }))}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#3b82f6',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    padding: '4px 0',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {expandedReplies[note.RfqNoteId] ? '▼' : '▶'} {note.Replies.length} {note.Replies.length === 1 ? 'reply' : 'replies'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Display replies */}
                                {note.Replies && note.Replies.length > 0 && expandedReplies[note.RfqNoteId] && (
                                    <div style={{ marginLeft: '30px', marginBottom: '10px' }}>
                                        {note.Replies.map((reply, replyIndex) => (
                                            <div key={replyIndex} style={{
                                                backgroundColor: '#ffffff',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                marginBottom: '8px',
                                                border: '1px solid #e5e7eb'
                                            }}>
                                                <div style={{
                                                    fontSize: '11px',
                                                    color: '#6b7280',
                                                    marginBottom: '6px'
                                                }}>
                                                    <strong style={{ color: '#374151', textTransform: 'capitalize' }}>
                                                        {reply.Username || 'Unknown'}
                                                    </strong>
                                                    {' • '}
                                                    {reply.CreatedDateTime ? new Date(reply.CreatedDateTime).toLocaleString('en-US') : 'Just now'}
                                                </div>
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: '#1f2937',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {renderMentionedText(reply.NoteText)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Reply input */}
                                {replyingTo && replyingTo.RfqNoteId === note.RfqNoteId && (
                                    <div style={{ marginLeft: '30px', marginBottom: '10px', position: 'relative' }}>
                                        <div style={{
                                            backgroundColor: '#f0f9ff',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            fontSize: '12px',
                                            color: '#374151'
                                        }}>
                                            Replying to <strong>{replyingTo.Username}</strong>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setReplyingTo(null);
                                                    setReplyText('');
                                                }}
                                                style={{
                                                    marginLeft: '10px',
                                                    padding: '2px 8px',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: '#6b7280',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <MentionsInput
                                            value={replyText}
                                            onChange={(e, newValue) => setReplyText(newValue)}
                                            placeholder="Enter reply... (use @ to mention users)"
                                            style={mentionStyle}
                                            className="mentions-input"
                                        >
                                            <Mention
                                                trigger="@"
                                                data={users}
                                                markup="@[__display__](__id__)"
                                                displayTransform={(id, display) => `@${display}`}
                                                style={{
                                                    backgroundColor: '#dbeafe',
                                                }}
                                                appendSpaceOnAdd
                                            />
                                        </MentionsInput>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (replyText.trim()) {
                                                    // Expand the replies section for this note
                                                    setExpandedReplies(prev => ({
                                                        ...prev,
                                                        [replyingTo.RfqNoteId]: true
                                                    }));
                                                    onAddReply(replyingTo, replyText.trim());
                                                    setReplyingTo(null);
                                                    setReplyText('');
                                                }
                                            }}
                                            style={{
                                                position: 'absolute',
                                                bottom: '8px',
                                                right: '8px',
                                                padding: '6px 14px',
                                                backgroundColor: '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                zIndex: 10
                                            }}
                                        >
                                            Reply
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    {notesList.length > 3 && !showAllNotes && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', marginBottom: '10px' }}>
                            <button
                                type="button"
                                onClick={onToggleShowAll}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: '#3b82f6',
                                    border: '1px solid #3b82f6',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Load more
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div style={{ position: 'relative', marginTop: '20px' }}>
                <MentionsInput
                    value={currentNote}
                    onChange={onCurrentNoteChange}
                    placeholder="Enter note... (use @ to mention users)"
                    style={mentionStyle}
                    a11ySuggestionsListLabel="Suggested users to mention"
                    className="mentions-input"
                >
                    <Mention
                        trigger="@"
                        data={users}
                        markup="@[__display__](__id__)"
                        displayTransform={(id, display) => `@${display}`}
                        style={{
                            backgroundColor: '#dbeafe',
                        }}
                        appendSpaceOnAdd
                    />
                </MentionsInput>
                <button
                    type="button"
                    onClick={onAddNote}
                    style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        padding: '6px 14px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        zIndex: 10
                    }}
                >
                    Done
                </button>
            </div>
        </div>
    );
};

export default NotesSection;
